import { getState, initAction } from "./declareAtom";
import { createCtx, Tree } from "./kernel";
import { assign, getIsAction, getIsAtom, getOwnKeys, getTree, safetyFunc, throwError, } from "./shared";
// TODO: try to use ES6 Map's instead of plain object
// for prevent using `delete` operator
// (need perf tests)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createStore(atom, initState) {
    let listeners = new Map();
    let nextListeners = listeners;
    let dispatchListeners = [];
    let nextDispatchListeners = dispatchListeners;
    let initialAtoms = new Set();
    let state = {};
    const storeTree = new Tree("store");
    if (atom !== undefined) {
        if (typeof atom === "object" && initState === undefined)
            assign(state, atom);
        else {
            if (!getIsAtom(atom))
                throwError("Invalid atom");
            if (typeof initState === "object" && initState !== null)
                assign(state, initState);
            else if (initState !== undefined)
                throwError("Invalid initial state");
            storeTree.union(getTree(atom));
            const ctx = createCtx(state, initAction);
            storeTree.forEach(initAction.type, ctx);
            assign(state, ctx.stateNew);
            initialAtoms = new Set(getOwnKeys(ctx.stateNew));
        }
    }
    function ensureCanMutateNextDispatchListeners() {
        if (nextDispatchListeners === dispatchListeners) {
            nextDispatchListeners = dispatchListeners.slice();
        }
    }
    function ensureCanMutateNextListeners(treeId) {
        if (nextListeners === listeners) {
            nextListeners = new Map();
            listeners.forEach((value, key) => nextListeners.set(key, treeId === key ? value.slice() : value));
        }
    }
    function _getState(target) {
        // TODO: try to cache `assign`
        if (target === undefined)
            return assign({}, state);
        if (!getIsAtom(target))
            throwError("Invalid target");
        const targetState = getState(state, target);
        if (targetState !== undefined)
            return targetState;
        const ctx = createCtx(state, initAction);
        getTree(target).forEach(initAction.type, ctx);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return getState(ctx.stateNew, target);
    }
    function subscribe(target, subscriber) {
        const listener = safetyFunc(subscriber || target, "listener");
        let isSubscribed = true;
        if (subscriber === undefined) {
            if (getIsAtom(listener) || getIsAction(listener))
                throwError("Invalid listener");
            ensureCanMutateNextDispatchListeners();
            nextDispatchListeners.push(listener);
            return () => {
                if (!isSubscribed)
                    return;
                isSubscribed = false;
                ensureCanMutateNextDispatchListeners();
                nextDispatchListeners.splice(nextDispatchListeners.indexOf(listener), 1);
            };
        }
        const isAction = getIsAction(target);
        if (!getIsAtom(target) && !isAction)
            throwError("Invalid subscription target");
        const targetTree = getTree(target);
        const targetId = targetTree.id;
        const isLazy = !isAction && !initialAtoms.has(targetId);
        ensureCanMutateNextListeners(targetId);
        if (!nextListeners.has(targetId)) {
            nextListeners.set(targetId, []);
            if (isLazy) {
                storeTree.union(targetTree);
                const ctx = createCtx(state, initAction);
                targetTree.forEach(initAction.type, ctx);
                assign(state, ctx.stateNew);
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        nextListeners.get(targetId).push(listener);
        return () => {
            if (!isSubscribed)
                return;
            isSubscribed = false;
            ensureCanMutateNextListeners(targetId);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const _listeners = nextListeners.get(targetId);
            _listeners.splice(_listeners.indexOf(listener), 1);
            if (isLazy && _listeners.length === 0) {
                nextListeners.delete(targetId);
                storeTree.disunion(targetTree, (id) => {
                    delete state[id];
                });
            }
        };
    }
    function dispatch(action) {
        const { type, payload, reactions } = action;
        if (typeof action !== "object" || action === null || typeof type !== "string")
            throwError("Invalid action");
        const ctx = createCtx(state, action);
        storeTree.forEach(type, ctx);
        const { changedIds, stateNew } = ctx;
        listeners = nextListeners;
        if (type === initAction.type)
            state = payload || {};
        if (changedIds.length > 0) {
            assign(state, stateNew);
            for (let i = 0; i < changedIds.length; i++) {
                const id = changedIds[i];
                callFromList(listeners.get(id) || [], stateNew[id]);
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        callFromList(reactions || [], payload, store);
        callFromList(listeners.get(type) || [], payload);
        callFromList((dispatchListeners = nextDispatchListeners), action, stateNew);
    }
    const bind = (actionCreator) => (...a) => 
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    store.dispatch(actionCreator(...a));
    const store = {
        getState: _getState,
        subscribe,
        dispatch,
        bind,
    };
    return store;
}
function callFromList(list, ...arg) {
    let i = -1;
    while (++i < list.length)
        list[i](...arg);
}
